import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client.js";
import type { AlarmRow } from "../components/AlarmBanner.js";
import type { Cluster } from "../components/ClusterCard.js";
import { Icon } from "../components/Icon.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * Il REGISTRO EVENTI di un impianto, portato da `LogsPage.qml`.
 *
 * Non e' il pannello allarmi ne' il grafico: e' il registro **forense**, quello
 * che si guarda dopo, quando bisogna ricostruire cosa e' successo e a che ora.
 * Per questo mette insieme tre fonti su un'unica linea del tempo:
 *
 *  - gli **allarmi**, con stato e priorita';
 *  - le **letture sopra la soglia di avviso** — solo quelle: sotto soglia
 *    sono migliaia di righe che seppellirebbero le altre due fonti;
 *  - il **registro di sistema**, cioe' apparati che vanno e vengono.
 *
 * Ogni riga di allarme che porta con se' la posizione della testa ha il
 * bottone che ci ripunta l'ottica. E' il motivo per cui quelle due colonne
 * vengono salvate: senza il bottone sono numeri che nessuno puo' usare.
 */

interface Reading {
  id: number | string;
  timestamp: string;
  ppm: number | null;
}

interface SystemLogRow {
  id: number;
  ts: string;
  category: string;
  level: string;
  msgKey: string;
  msgParam: string | null;
}

type Entry =
  | { kind: "alarm"; ts: string; row: AlarmRow }
  | { kind: "ppm"; ts: string; ppm: number | null }
  | { kind: "system"; ts: string; row: SystemLogRow };

const POLL_MS = 10_000;

export function LogsPage({ cluster, onBack }: { cluster: Cluster; onBack: () => void }) {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [alarms, setAlarms] = useState<AlarmRow[]>([]);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [system, setSystem] = useState<SystemLogRow[]>([]);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  const l1 = cluster.thresholdL1 ?? 100;
  const l2 = cluster.thresholdL2 ?? 500;

  const load = useCallback(async () => {
    try {
      const [a, r, s] = await Promise.all([
        api.call<AlarmRow[]>("alarms.list"),
        api.call<Reading[]>("emissions.readings", {
          clusterId: cluster.id,
          // Tutta la storia: il default della rotta e' l'ultima ora, che su
          // un registro forense vuol dire quasi sempre niente.
          since: "2000-01-01T00:00:00.000Z",
        }),
        api.call<SystemLogRow[]>("system-log.findByCluster", {
          clusterId: cluster.id,
          limit: 500,
        }),
      ]);
      setAlarms(a.filter((x) => x.clusterId === cluster.id));
      setReadings(r.filter((x) => Number(x.ppm) >= l1));
      setSystem(s);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api, cluster.id, l1]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [
      ...alarms.map((row) => ({ kind: "alarm", ts: row.createdAt, row }) as const),
      ...readings.map((r) => ({ kind: "ppm", ts: r.timestamp, ppm: r.ppm }) as const),
      ...system.map((row) => ({ kind: "system", ts: row.ts, row }) as const),
    ];
    return out.sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
  }, [alarms, readings, system]);

  const goTo = async (row: AlarmRow) => {
    try {
      const r = await api.call<{ moved?: boolean; verified?: boolean }>("ptz.gotoPosition", {
        clusterId: cluster.id,
        pan: row.ptzPan,
        tilt: row.ptzTilt,
      });
      // Il rig puo' accettare il comando e non muoversi: su UDP nessuno
      // smentisce. Dirlo evita che l'operatore guardi un'inquadratura ferma
      // convinto di essere puntato sulla perdita.
      const name = cluster.name ?? "";
      setToast(
        r.verified === false || r.moved
          ? t("syslog.ptzGotoAlarm", { p: name })
          : t("syslog.ptzGotoNoMove", { p: name }),
      );
    } catch (e) {
      setToast(e instanceof ApiError ? e.message : String(e));
    }
    setTimeout(() => setToast(""), 4000);
  };

  const download = () => {
    const clusterName = cluster.name || "—";
    const now = new Date().toISOString();
    const pad = (s: string, w: number) => (s.length > w ? s.slice(0, w) : s.padEnd(w));
    const padL = (s: string, w: number) => (s.length > w ? s.slice(-w) : s.padStart(w));
    const lines = [
      "LMSmini — Event Log Export",
      `Generated: ${now}`,
      `Cluster:   ${clusterName} (${cluster.id})`,
      `Counts:    ${alarms.length} alarms · ${readings.length} ppm readings · ${system.length} system events`,
      `Thresholds: L1=${Math.round(l1)}  L2=${Math.round(l2)}`,
      "",
      "=".repeat(110),
      "DATE       TIME      TYPE       DETAILS                                                         PPM·M",
      "-".repeat(110),
    ];
    for (const e of entries) {
      const d = new Date(e.ts);
      const bad = Number.isNaN(d.getTime());
      const date = bad ? "—" : d.toLocaleDateString("en-GB");
      const time = bad ? "—" : d.toLocaleTimeString("en-GB", { hour12: false });
      let type = "—";
      let details = "";
      let ppm = "—";
      if (e.kind === "alarm") {
        type = `ALARM ${e.row.level ?? ""}`;
        details = `state=${e.row.state ?? "?"} priority=${e.row.priority ?? "?"}`;
        ppm = e.row.ppmValue === null ? "—" : Number(e.row.ppmValue).toFixed(1);
      } else if (e.kind === "ppm") {
        type = "PPM";
        details = "Sensor reading";
        ppm = e.ppm === null ? "—" : Number(e.ppm).toFixed(1);
      } else {
        type = `SYS ${e.row.level || "INFO"}`;
        details = `[${e.row.category}] ${t(e.row.msgKey, { p: e.row.msgParam ?? "" })}`;
      }
      lines.push(pad(date, 11) + pad(time, 10) + pad(type, 11) + pad(details, 65) + padL(ppm, 8));
    }
    lines.push("=".repeat(110), `End of log — ${entries.length} entries`);

    const safe = clusterName.replace(/[^a-zA-Z0-9_-]/g, "_");
    const stamp = now.replace(/[:.]/g, "-").slice(0, 19);
    const blob = new Blob([`${lines.join("\n")}\n`], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lmsmini_log_${safe}_${stamp}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = async () => {
    setConfirmClear(false);
    try {
      await api.call("system-log.deleteAll");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="logs">
      <header className="logs__head">
        <button className="users__back" onClick={onBack}>
          <Icon name="arrow-left" size={20} />
        </button>
        <div>
          <h2>{t("hdr.eventLog")}</h2>
          <span className="users__sub">{cluster.name}</span>
        </div>
        <span className="emissions__spacer" />
        <span className="logs__count logs__count--alarm">{alarms.length} ALARMS</span>
        <span className="logs__count">{readings.length} PPM READINGS</span>
        <span className="logs__sep" />
        <span className="logs__count">
          L1={Math.round(l1)} L2={Math.round(l2)}
        </span>
        <button className="logs__dl" onClick={download}>
          <Icon name="download" size={14} />
          {t("logs.downloadBtn")}
        </button>
        {can("deleteSystemLog") && (
          <button className="logs__del" onClick={() => setConfirmClear(true)}>
            {t("logs.deleteBtn")}
          </button>
        )}
      </header>

      {error && <p className="alert">{error}</p>}

      {/* Cancellare il registro e' irreversibile, e nel `.qml` la domanda
          e' una **finestra modale** che copre la pagina: una striscia in
          mezzo al contenuto la si conferma per inerzia, scorrendo. Il numero
          di righe che sparisce sta nel testo — non un "sei sicuro?". */}
      {confirmClear && (
        <div className="modal" onClick={() => setConfirmClear(false)}>
          <div className="modal__card" onClick={(e) => e.stopPropagation()}>
            <div className="modal__head">
              <Icon name="alert-triangle" size={28} />
              <b>{t("logs.confirmTitle")}</b>
            </div>
            <p className="muted small">{t("logs.confirmMsg", { p: system.length })}</p>
            <div className="modal__acts">
              <button className="modal__cancel" onClick={() => setConfirmClear(false)}>
                {t("logs.cancel")}
              </button>
              <button className="modal__danger" onClick={() => void clearAll()}>
                {t("logs.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registro e intestazione stanno in 900 px centrati, come nel `.qml`:
          a tutta larghezza l'ora e la concentrazione della stessa riga
          finiscono a un metro di distanza, e per leggerle insieme si deve
          seguire la riga con il dito. */}
      <div className="logs__table">
        <div className="logs__row logs__row--head">
          <span>DATE</span>
          <span>TIME</span>
          <span>TYPE</span>
          <span className="logs__details">DETAILS</span>
          <span className="logs__goto" />
          <span className="ta-right">PPM·M</span>
        </div>

        {entries.map((e, i) => (
          <LogLine
            key={`${e.kind}-${e.ts}-${i}`}
            entry={e}
            canGoTo={can("controlPtz")}
            onGoTo={goTo}
          />
        ))}

        {entries.length === 0 && <p className="muted logs__empty">{t("logs.noEvents")}</p>}
      </div>

      {/* Il comando puo' fallire in silenzio — il rig accetta il pacchetto e
          non si muove — quindi l'esito si dice. Sta in basso al centro come
          nel `.qml`, sopra il registro: e' una risposta, non una riga nuova. */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function LogLine({
  entry,
  canGoTo,
  onGoTo,
}: {
  entry: Entry;
  canGoTo: boolean;
  onGoTo: (row: AlarmRow) => void;
}) {
  const { t } = useI18n();
  const d = new Date(entry.ts);
  const bad = Number.isNaN(d.getTime());

  const critical = entry.kind === "alarm" && entry.row.level === "L2";
  const tone =
    entry.kind === "alarm"
      ? critical
        ? "critical"
        : "warning"
      : entry.kind === "system"
        ? entry.row.level === "WARNING"
          ? "warning"
          : "sys"
        : "ppm";

  const badge =
    entry.kind === "alarm"
      ? (entry.row.level ?? "ALARM")
      : entry.kind === "system"
        ? entry.row.category || "SYS"
        : "PPM";

  const details =
    entry.kind === "alarm"
      ? `${entry.row.state ?? ""}${entry.row.priority ? `  •  ${entry.row.priority}` : ""}`
      : entry.kind === "system"
        ? t(entry.row.msgKey, { p: entry.row.msgParam ?? "" })
        : "—";

  const ppm =
    entry.kind === "alarm"
      ? entry.row.ppmValue
      : entry.kind === "ppm"
        ? entry.ppm
        : null;

  // Il bottone c'e' solo dove ha senso: riga di allarme, coordinate salvate e
  // permesso di muovere la testa.
  const showGoTo =
    entry.kind === "alarm" &&
    canGoTo &&
    entry.row.ptzPan !== undefined &&
    entry.row.ptzPan !== null &&
    entry.row.ptzTilt !== undefined &&
    entry.row.ptzTilt !== null;

  return (
    <div className={`logs__row logs__row--${tone}`}>
      <span className="mono">{bad ? "—" : d.toLocaleDateString("en-GB")}</span>
      <span className="mono">{bad ? "—" : d.toLocaleTimeString("en-GB", { hour12: false })}</span>
      <span className={`logbadge logbadge--${tone}`}>{badge}</span>
      <span className="logs__details">{details}</span>
      <span className="logs__goto">
        {showGoTo && entry.kind === "alarm" && (
          <button onClick={() => onGoTo(entry.row)}>{t("evt.goToLeak")}</button>
        )}
      </span>
      <span className="ta-right mono">{ppm === null || ppm === undefined ? "—" : Number(ppm).toFixed(1)}</span>
    </div>
  );
}
